import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { FileUpload } from "@/components/ui/file-upload";
import {
  Stethoscope, Certificate, Target, ChatCenteredDots, ShieldCheck,
  Plus, CheckCircle, Trash,
} from "@phosphor-icons/react";
import { fmtDate } from "@/lib/helpers";

import { devLog } from "@/lib/devLog";
/**
 * Phase 5 CRM tab — one component, 5 sub-cards.
 *
 *   1. Diagnoses (embedded on the student, ICD-10 quick-picker)
 *   2. IEP versioning (list of versions + goals per version)
 *   3. Milestones (linked to IEP goals optionally, achievable)
 *   4. Communications log (phone/whatsapp/email/etc + follow-ups)
 *   5. Consents (photo/video/telehealth/data-sharing, expiry-aware)
 *
 * Guardians see a read-only view; anyone else with Owner/Admin/Reception
 * can create/edit. Therapists can read only (mirrors backend gate).
 */
export default function StudentCrmTab({ studentId }) {
  const { user } = useAuth();
  const roles = user?.roles || [];
  const canWrite = ["owner", "admin", "reception"].some(r => roles.includes(r));

  const [summary, setSummary] = useState(null);

  const refreshSummary = async () => {
    try {
      const { data } = await api.get(`/crm/students/${studentId}/summary`);
      setSummary(data);
    } catch (e) { devLog(e); }
  };
  useEffect(() => { refreshSummary(); }, [studentId]);

  return (
    <div className="space-y-4" data-testid="student-crm-tab">
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <SummaryChip label="Diagnoses" value={summary.diagnoses_count} icon={Stethoscope} testid="crm-sum-diagnoses" />
          <SummaryChip label={`IEP v${summary.iep.active_version || "—"}`} value={summary.iep.goal_count || 0} icon={Certificate} sub="goals" testid="crm-sum-iep" />
          <SummaryChip label="Milestones" value={`${summary.milestones.achieved}/${summary.milestones.total}`} icon={Target} testid="crm-sum-milestones" />
          <SummaryChip label="Follow-ups" value={summary.comms.open_follow_ups} icon={ChatCenteredDots} tone={summary.comms.open_follow_ups > 0 ? "amber" : "muted"} testid="crm-sum-followups" />
          <SummaryChip label="Consents" value={summary.consents.active} icon={ShieldCheck} testid="crm-sum-consents" />
        </div>
      )}

      <DiagnosesCard studentId={studentId} canWrite={canWrite} onChange={refreshSummary} />
      <IepCard studentId={studentId} canWrite={canWrite} onChange={refreshSummary} />
      <MilestonesCard studentId={studentId} canWrite={canWrite} onChange={refreshSummary} />
      <CommsCard studentId={studentId} canWrite={canWrite} onChange={refreshSummary} />
      <ConsentsCard studentId={studentId} canWrite={canWrite} onChange={refreshSummary} />
    </div>
  );
}


function SummaryChip({ label, value, icon: Icon, sub, tone = "default", testid }) {
  const tones = {
    default: "border-border",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    muted: "border-border",
  };
  return (
    <div className={`rounded-md border ${tones[tone]} p-2.5`} data-testid={testid}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground">
        {Icon && <Icon size={11} weight="duotone" />}{label}
      </div>
      <div className="font-display text-xl font-bold tabular-nums leading-tight mt-0.5">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
    </div>
  );
}


// ============================================================================
// 1. DIAGNOSES
// ============================================================================
function DiagnosesCard({ studentId, canWrite, onChange }) {
  const [rows, setRows] = useState([]);
  const [quick, setQuick] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", label: "", source: "ICD-10", diagnosed_on: "", notes: "" });

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get(`/crm/students/${studentId}/diagnoses`),
        api.get("/crm/diagnoses/quick-list"),
      ]);
      setRows(a.data || []); setQuick(b.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [studentId]);

  const add = async () => {
    if (!form.code || !form.label) return toast.error("Code and label required");
    try {
      await api.post(`/crm/students/${studentId}/diagnoses`, form);
      toast.success("Diagnosis added");
      setOpen(false);
      setForm({ code: "", label: "", source: "ICD-10", diagnosed_on: "", notes: "" });
      await load(); onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this diagnosis?")) return;
    try { await api.delete(`/crm/students/${studentId}/diagnoses/${id}`); await load(); onChange?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const pickQuick = (item) => setForm(f => ({ ...f, code: item.code, label: item.label, source: item.source }));

  return (
    <Card data-testid="crm-diagnoses-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base font-display">
          <Stethoscope size={16} weight="duotone" /> Diagnoses
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        {rows.length === 0 && (
          <p className="text-sm text-muted-foreground">No structured diagnoses recorded.</p>
        )}
        {rows.map(d => (
          <div key={d.id} className="flex items-start justify-between gap-2 rounded-md border border-border p-2.5">
            <div className="flex-1">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">{d.code}</span>
                <span className="font-medium text-sm">{d.label}</span>
                <Badge variant="outline" className="text-[10px]">{d.source}</Badge>
              </div>
              {d.diagnosed_on && <div className="text-[11px] text-muted-foreground mt-1">Diagnosed {fmtDate(d.diagnosed_on)}</div>}
              {d.notes && <div className="text-xs mt-1">{d.notes}</div>}
            </div>
            {canWrite && (
              <Button variant="ghost" size="sm" onClick={() => remove(d.id)} className="h-7 w-7 p-0 text-red-700" data-testid={`diag-del-${d.id}`}>
                <Trash size={13} />
              </Button>
            )}
          </div>
        ))}
        {canWrite && (
          <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="gap-1.5" data-testid="add-diagnosis-btn">
            <Plus size={13} /> Add diagnosis
          </Button>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg bg-card">
          <DialogHeader><DialogTitle>Add diagnosis</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs mb-1 block">Quick-pick from ICD-10</Label>
              <div className="max-h-40 overflow-y-auto border border-border rounded-md p-1 space-y-0.5">
                {quick.map(q => (
                  <button key={q.code} type="button"
                    onClick={() => pickQuick(q)}
                    className="w-full text-left px-2 py-1 rounded hover:bg-muted text-xs flex items-baseline gap-2"
                    data-testid={`quick-diag-${q.code}`}
                  >
                    <span className="font-mono bg-muted px-1 rounded text-[10px]">{q.code}</span>
                    <span className="truncate">{q.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Code</Label><Input value={form.code} onChange={e => setForm({...form, code: e.target.value})} data-testid="diag-code" /></div>
              <div><Label className="text-xs">Source</Label>
                <Select value={form.source} onValueChange={(v) => setForm({...form, source: v})}>
                  <SelectTrigger data-testid="diag-source"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="ICD-10">ICD-10</SelectItem><SelectItem value="DSM-5">DSM-5</SelectItem><SelectItem value="custom">Custom</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs">Label</Label><Input value={form.label} onChange={e => setForm({...form, label: e.target.value})} data-testid="diag-label" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Diagnosed on</Label><Input type="date" value={form.diagnosed_on} onChange={e => setForm({...form, diagnosed_on: e.target.value})} data-testid="diag-date" /></div>
            </div>
            <div><Label className="text-xs">Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} data-testid="diag-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={add} data-testid="diag-save">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


// ============================================================================
// 2. IEP VERSIONING
// ============================================================================
function IepCard({ studentId, canWrite, onChange }) {
  const [ieps, setIeps] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const emptyForm = { effective_from: "", effective_to: "", summary: "", review_date: "", goals: [] };
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    try { const { data } = await api.get(`/crm/students/${studentId}/ieps`); setIeps(data || []); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [studentId]);

  const addGoal = () => setForm(f => ({ ...f, goals: [...f.goals, { area: "cognitive", description: "", target: "", measurement: "", status: "in_progress" }] }));
  const rmGoal = (idx) => setForm(f => ({ ...f, goals: f.goals.filter((_, i) => i !== idx) }));
  const setGoal = (idx, k, v) => setForm(f => ({ ...f, goals: f.goals.map((g, i) => i === idx ? { ...g, [k]: v } : g) }));

  const openCreate = () => { setEditingId(null); setForm({ ...emptyForm, effective_from: new Date().toISOString().slice(0, 10) }); setOpen(true); };
  const openEdit = (iep) => { setEditingId(iep.id); setForm({ ...iep }); setOpen(true); };

  const save = async () => {
    if (!form.effective_from) return toast.error("Effective-from date is required");
    if (form.goals.length === 0) return toast.error("Add at least one goal");
    try {
      if (editingId) {
        await api.put(`/crm/ieps/${editingId}`, form);
        toast.success("IEP updated");
      } else {
        await api.post(`/crm/students/${studentId}/ieps`, form);
        toast.success("New IEP version created");
      }
      setOpen(false); await load(); onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const archive = async (id) => {
    if (!window.confirm("Archive this IEP version?")) return;
    try { await api.post(`/crm/ieps/${id}/archive`); await load(); onChange?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Card data-testid="crm-iep-card">
      <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base font-display">
          <Certificate size={16} weight="duotone" /> IEP versions
        </CardTitle>
        {canWrite && (
          <Button variant="outline" size="sm" onClick={openCreate} className="gap-1.5" data-testid="new-iep-btn">
            <Plus size={13} /> New version
          </Button>
        )}
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        {ieps.length === 0 && <p className="text-sm text-muted-foreground">No IEP recorded yet.</p>}
        {ieps.map(iep => (
          <div key={iep.id} className="rounded-md border border-border p-3" data-testid={`iep-row-${iep.version}`}>
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div>
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-lg font-bold">v{iep.version}</span>
                  {iep.is_active
                    ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">Active</Badge>
                    : <Badge variant="outline" className="text-[10px]">Archived</Badge>}
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  {fmtDate(iep.effective_from)} → {iep.effective_to ? fmtDate(iep.effective_to) : "ongoing"}
                  {iep.review_date && <> · review {fmtDate(iep.review_date)}</>}
                </div>
              </div>
              {canWrite && iep.is_active && (
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(iep)} className="h-7 px-2 text-xs">Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => archive(iep.id)} className="h-7 px-2 text-xs text-red-700">Archive</Button>
                </div>
              )}
            </div>
            {iep.summary && <p className="text-xs mt-2 text-muted-foreground">{iep.summary}</p>}
            <div className="mt-2 space-y-1">
              {(iep.goals || []).map(g => (
                <div key={g.id} className="text-xs flex items-start gap-2 p-1.5 rounded bg-muted/50">
                  <Badge variant="outline" className="text-[9px] uppercase shrink-0">{g.area}</Badge>
                  <div className="flex-1">
                    <div>{g.description}</div>
                    {g.target && <div className="text-[10px] text-muted-foreground">Target: {g.target}</div>}
                  </div>
                  <Badge variant="outline" className={`text-[9px] ${g.status === "met" ? "border-emerald-200 text-emerald-700" : ""}`}>{g.status}</Badge>
                </div>
              ))}
            </div>
          </div>
        ))}
      </CardContent>

      {/* IEP editor */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl bg-card max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? "Edit IEP" : "New IEP version"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-xs">Effective from</Label><Input type="date" value={form.effective_from || ""} onChange={e => setForm({...form, effective_from: e.target.value})} data-testid="iep-from" /></div>
              <div><Label className="text-xs">Effective to</Label><Input type="date" value={form.effective_to || ""} onChange={e => setForm({...form, effective_to: e.target.value})} /></div>
              <div><Label className="text-xs">Review date</Label><Input type="date" value={form.review_date || ""} onChange={e => setForm({...form, review_date: e.target.value})} /></div>
            </div>
            <div><Label className="text-xs">Summary</Label><Textarea rows={2} value={form.summary || ""} onChange={e => setForm({...form, summary: e.target.value})} /></div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-xs">Goals ({form.goals.length})</Label>
                <Button variant="outline" size="sm" onClick={addGoal} className="h-7 gap-1 text-xs" data-testid="add-iep-goal"><Plus size={12} /> Add goal</Button>
              </div>
              <div className="space-y-2">
                {form.goals.map((g, i) => (
                  <div key={g.id || i} className="rounded-md border border-border p-2 space-y-2">
                    <div className="grid grid-cols-3 gap-2">
                      <Select value={g.area} onValueChange={(v) => setGoal(i, "area", v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["motor","speech","cognitive","social","adl","academic"].map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Select value={g.status || "in_progress"} onValueChange={(v) => setGoal(i, "status", v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="in_progress">In progress</SelectItem>
                          <SelectItem value="met">Met</SelectItem>
                          <SelectItem value="discontinued">Discontinued</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button variant="ghost" size="sm" onClick={() => rmGoal(i)} className="h-9 text-red-700"><Trash size={13} /></Button>
                    </div>
                    <Input placeholder="Description" value={g.description || ""} onChange={e => setGoal(i, "description", e.target.value)} data-testid={`iep-goal-desc-${i}`} />
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="Target (e.g. 5x/day)" value={g.target || ""} onChange={e => setGoal(i, "target", e.target.value)} />
                      <Input placeholder="Measurement (e.g. session log)" value={g.measurement || ""} onChange={e => setGoal(i, "measurement", e.target.value)} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="iep-save">{editingId ? "Save" : "Create version"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


// ============================================================================
// 3. MILESTONES
// ============================================================================
function MilestonesCard({ studentId, canWrite, onChange }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", category: "cognitive", target_date: "", notes: "", iep_goal_id: "" });
  const [activeIepGoals, setActiveIepGoals] = useState([]);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get(`/crm/students/${studentId}/milestones`),
        api.get(`/crm/students/${studentId}/ieps`),
      ]);
      setRows(a.data || []);
      const active = (b.data || []).find(i => i.is_active);
      setActiveIepGoals(active ? active.goals || [] : []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [studentId]);

  const add = async () => {
    if (!form.title) return toast.error("Title is required");
    try {
      // Empty string iep_goal_id → null so backend Optional works
      const body = { ...form, iep_goal_id: form.iep_goal_id || null };
      await api.post(`/crm/students/${studentId}/milestones`, body);
      toast.success("Milestone added");
      setOpen(false); setForm({ title: "", category: "cognitive", target_date: "", notes: "", iep_goal_id: "" });
      await load(); onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const achieve = async (id) => {
    try { await api.post(`/crm/milestones/${id}/achieve`, {}); toast.success("Marked achieved"); await load(); onChange?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this milestone?")) return;
    try { await api.delete(`/crm/milestones/${id}`); await load(); onChange?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Card data-testid="crm-milestones-card">
      <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base font-display">
          <Target size={16} weight="duotone" /> Milestones
        </CardTitle>
        {canWrite && (
          <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="gap-1.5" data-testid="new-milestone-btn">
            <Plus size={13} /> Add
          </Button>
        )}
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        {rows.length === 0 && <p className="text-sm text-muted-foreground">No milestones yet.</p>}
        {rows.map(m => (
          <div key={m.id} className="rounded-md border border-border p-2.5" data-testid={`milestone-row-${m.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium">{m.title}</span>
                  <Badge variant="outline" className="text-[10px] uppercase">{m.category}</Badge>
                  {m.status === "achieved"
                    ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">Achieved</Badge>
                    : <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">Pending</Badge>}
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">
                  {m.target_date && <>Target {fmtDate(m.target_date)} · </>}
                  {m.status === "achieved" && m.achieved_on && <>Achieved {fmtDate(m.achieved_on)}</>}
                </div>
                {m.notes && <div className="text-xs mt-1">{m.notes}</div>}
              </div>
              {canWrite && (
                <div className="flex gap-1">
                  {m.status !== "achieved" && (
                    <Button size="sm" variant="ghost" onClick={() => achieve(m.id)} className="h-7 px-2 text-xs text-emerald-700" data-testid={`milestone-achieve-${m.id}`}>
                      <CheckCircle size={13} /> Achieve
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => remove(m.id)} className="h-7 w-7 p-0 text-red-700"><Trash size={13} /></Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md bg-card">
          <DialogHeader><DialogTitle>Add milestone</DialogTitle></DialogHeader>
          <div className="space-y-2.5">
            <div><Label className="text-xs">Title</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="milestone-title" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["motor","speech","cognitive","social","adl","academic"].map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Target date</Label><Input type="date" value={form.target_date} onChange={e => setForm({...form, target_date: e.target.value})} /></div>
            </div>
            {activeIepGoals.length > 0 && (
              <div><Label className="text-xs">Link to IEP goal (optional)</Label>
                <Select value={form.iep_goal_id || "none"} onValueChange={(v) => setForm({...form, iep_goal_id: v === "none" ? "" : v})}>
                  <SelectTrigger data-testid="milestone-iep-goal"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— none —</SelectItem>
                    {activeIepGoals.map(g => <SelectItem key={g.id} value={g.id}>[{g.area}] {g.description.slice(0, 50)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div><Label className="text-xs">Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={add} data-testid="milestone-save">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


// ============================================================================
// 4. COMMS LOG
// ============================================================================
function CommsCard({ studentId, canWrite, onChange }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ direction: "outbound", channel: "phone", summary: "", follow_up_required: false, follow_up_date: "" });

  const load = async () => {
    try { const { data } = await api.get(`/crm/students/${studentId}/comms`); setRows(data || []); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [studentId]);

  const add = async () => {
    if (!form.summary) return toast.error("Summary is required");
    try {
      await api.post(`/crm/students/${studentId}/comms`, form);
      toast.success("Communication logged");
      setOpen(false); setForm({ direction: "outbound", channel: "phone", summary: "", follow_up_required: false, follow_up_date: "" });
      await load(); onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const closeFu = async (id) => {
    try { await api.post(`/crm/comms/${id}/close-follow-up`, {}); await load(); onChange?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try { await api.delete(`/crm/comms/${id}`); await load(); onChange?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Card data-testid="crm-comms-card">
      <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base font-display">
          <ChatCenteredDots size={16} weight="duotone" /> Communications log
        </CardTitle>
        {canWrite && (
          <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="gap-1.5" data-testid="new-comm-btn">
            <Plus size={13} /> Log
          </Button>
        )}
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        {rows.length === 0 && <p className="text-sm text-muted-foreground">No communications logged.</p>}
        {rows.map(c => (
          <div key={c.id} className="rounded-md border border-border p-2.5" data-testid={`comm-row-${c.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Badge variant="outline" className="text-[10px] uppercase">{c.direction}</Badge>
                  <Badge variant="outline" className="text-[10px] uppercase">{c.channel}</Badge>
                  <span className="text-[11px] text-muted-foreground">{fmtDate(c.occurred_at)}</span>
                  {c.logged_by_name && <span className="text-[11px] text-muted-foreground">· by {c.logged_by_name}</span>}
                </div>
                <div className="text-sm mt-1">{c.summary}</div>
                {c.notes && <div className="text-xs text-muted-foreground mt-1">{c.notes}</div>}
                {c.follow_up_required && (
                  <div className="mt-1.5 flex items-center gap-1.5 text-[11px]">
                    {c.follow_up_closed
                      ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">Follow-up closed</Badge>
                      : <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">Follow-up by {c.follow_up_date ? fmtDate(c.follow_up_date) : "—"}</Badge>}
                    {canWrite && !c.follow_up_closed && (
                      <Button size="sm" variant="ghost" onClick={() => closeFu(c.id)} className="h-6 px-1.5 text-[10px] text-emerald-700" data-testid={`comm-close-${c.id}`}>Close</Button>
                    )}
                  </div>
                )}
              </div>
              {canWrite && (
                <Button size="sm" variant="ghost" onClick={() => remove(c.id)} className="h-7 w-7 p-0 text-red-700"><Trash size={13} /></Button>
              )}
            </div>
          </div>
        ))}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md bg-card">
          <DialogHeader><DialogTitle>Log communication</DialogTitle></DialogHeader>
          <div className="space-y-2.5">
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Direction</Label>
                <Select value={form.direction} onValueChange={(v) => setForm({...form, direction: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="outbound">Outbound</SelectItem><SelectItem value="inbound">Inbound</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Channel</Label>
                <Select value={form.channel} onValueChange={(v) => setForm({...form, channel: v})}>
                  <SelectTrigger data-testid="comm-channel"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["phone","whatsapp","email","in_person","sms","other"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs">Summary</Label><Input value={form.summary} onChange={e => setForm({...form, summary: e.target.value})} data-testid="comm-summary" /></div>
            <div className="flex items-center gap-2 pt-1">
              <input id="fu" type="checkbox" checked={form.follow_up_required} onChange={e => setForm({...form, follow_up_required: e.target.checked})} data-testid="comm-fu-req" />
              <Label htmlFor="fu" className="text-xs cursor-pointer">Follow-up required</Label>
            </div>
            {form.follow_up_required && (
              <div><Label className="text-xs">Follow-up date</Label><Input type="date" value={form.follow_up_date} onChange={e => setForm({...form, follow_up_date: e.target.value})} data-testid="comm-fu-date" /></div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={add} data-testid="comm-save">Log</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


// ============================================================================
// 5. CONSENTS
// ============================================================================
function ConsentsCard({ studentId, canWrite, onChange }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const emptyForm = { consent_type: "photo", status: "granted", granted_on: "", expires_on: "", signed_by: "", signature_method: "written", document_file_id: null, notes: "" };
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    try { const { data } = await api.get(`/crm/students/${studentId}/consents`); setRows(data || []); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [studentId]);

  const add = async () => {
    try {
      await api.post(`/crm/students/${studentId}/consents`, form);
      toast.success("Consent recorded");
      setOpen(false); setForm(emptyForm);
      await load(); onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const withdraw = async (id) => {
    const reason = window.prompt("Withdrawal reason? (optional)");
    try { await api.post(`/crm/consents/${id}/withdraw`, { reason }); await load(); onChange?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this consent record?")) return;
    try { await api.delete(`/crm/consents/${id}`); await load(); onChange?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const statusColor = (s) => ({
    granted: "bg-emerald-100 text-emerald-800 border-emerald-200",
    denied: "bg-red-100 text-red-800 border-red-200",
    withdrawn: "bg-muted text-muted-foreground",
  }[s] || "");

  return (
    <Card data-testid="crm-consents-card">
      <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base font-display">
          <ShieldCheck size={16} weight="duotone" /> Consents
        </CardTitle>
        {canWrite && (
          <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="gap-1.5" data-testid="new-consent-btn">
            <Plus size={13} /> Record
          </Button>
        )}
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        {rows.length === 0 && <p className="text-sm text-muted-foreground">No consents on file.</p>}
        {rows.map(c => (
          <div key={c.id} className="rounded-md border border-border p-2.5" data-testid={`consent-row-${c.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Badge variant="outline" className="text-[10px] uppercase">{c.consent_type.replace(/_/g, " ")}</Badge>
                  <Badge className={`${statusColor(c.status)} text-[10px]`}>{c.status}</Badge>
                  {c.signature_method && <span className="text-[10px] text-muted-foreground">· {c.signature_method}</span>}
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">
                  Granted {fmtDate(c.granted_on)}
                  {c.signed_by && <> · by {c.signed_by}</>}
                  {c.expires_on && <> · expires {fmtDate(c.expires_on)}</>}
                </div>
                {c.notes && <div className="text-xs mt-1">{c.notes}</div>}
                {c.status === "withdrawn" && c.withdrawn_reason && <div className="text-xs mt-1 text-red-700">Withdrawn: {c.withdrawn_reason}</div>}
              </div>
              {canWrite && (
                <div className="flex gap-1">
                  {c.status === "granted" && (
                    <Button size="sm" variant="ghost" onClick={() => withdraw(c.id)} className="h-7 px-2 text-xs text-amber-700" data-testid={`consent-withdraw-${c.id}`}>Withdraw</Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => remove(c.id)} className="h-7 w-7 p-0 text-red-700"><Trash size={13} /></Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md bg-card">
          <DialogHeader><DialogTitle>Record consent</DialogTitle></DialogHeader>
          <div className="space-y-2.5">
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Type</Label>
                <Select value={form.consent_type} onValueChange={(v) => setForm({...form, consent_type: v})}>
                  <SelectTrigger data-testid="consent-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["photo","video","telehealth","data_sharing","media_release","other"].map(x => <SelectItem key={x} value={x}>{x.replace(/_/g, " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="granted">Granted</SelectItem><SelectItem value="denied">Denied</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Granted on</Label><Input type="date" value={form.granted_on} onChange={e => setForm({...form, granted_on: e.target.value})} /></div>
              <div><Label className="text-xs">Expires on</Label><Input type="date" value={form.expires_on} onChange={e => setForm({...form, expires_on: e.target.value})} /></div>
            </div>
            <div><Label className="text-xs">Signed by</Label><Input value={form.signed_by} onChange={e => setForm({...form, signed_by: e.target.value})} placeholder="Guardian name" /></div>
            <div><Label className="text-xs">Signature method</Label>
              <Select value={form.signature_method} onValueChange={(v) => setForm({...form, signature_method: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="verbal">Verbal</SelectItem><SelectItem value="written">Written</SelectItem><SelectItem value="e-sign">E-sign</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Signed document (optional)</Label>
              <FileUpload
                fileId={form.document_file_id}
                onFileChange={(fid) => setForm({...form, document_file_id: fid})}
                entityType="consent"
                entityId={studentId}
                accept="image/*,application/pdf"
                data-testid="consent-file"
              />
            </div>
            <div><Label className="text-xs">Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={add} data-testid="consent-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
